import axios from 'axios';
import {
  isSessionAuthError,
  isJwtExpired,
  shouldSkipSessionCheck,
  triggerSessionExpired,
  SESSION_EXPIRED_MESSAGE,
} from '../utils/sessionAuth';

// При доступе с другого устройства (по локалке) используем тот же хост — запросы идут через Vite proxy
const API_BASE_URL = import.meta.env.VITE_API_URL ?? (typeof window !== 'undefined' ? `${window.location.origin}/api` : 'http://localhost:5000/api');

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json'
  },
  timeout: 10000 // 10 секунд таймаут
});

// Добавляем токен к каждому запросу
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    if (
      token
      && !shouldSkipSessionCheck(config.url)
      && isJwtExpired(token)
    ) {
      triggerSessionExpired(SESSION_EXPIRED_MESSAGE);
      return Promise.reject(new axios.CanceledError('Session expired'));
    }

    // Idempotency-Key для POST/PUT/PATCH.
    // Ключ детерминированный — из тела + URL + userId. Если пользователь
    // повторно нажмёт «Создать» (например, после потери VPN-соединения),
    // тело запроса будет идентичным → серверный middleware вернёт ответ
    // первого запроса вместо создания дубля.
    // Для FormData (загрузка файлов) и пустых тел — fallback на случайный ключ,
    // дубли там и так не создают карточек.
    const method = (config.method || 'GET').toUpperCase();
    if (['POST', 'PUT', 'PATCH'].includes(method) && !config.headers['Idempotency-Key']) {
      config.headers['Idempotency-Key'] = buildIdempotencyKey(config);
    }

    // Глобальный Content-Type: application/json ломает multipart: boundary не подставляется — на iOS Safari запросы загрузки файлов падают странно.
    if (config.data instanceof FormData) {
      const h = config.headers;
      if (h && typeof h.delete === 'function') {
        h.delete('Content-Type');
      } else if (h) {
        delete h['Content-Type'];
      }
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

/** Простой 32-битный хеш (для дедупликации, не криптография). */
function stableHash(str) {
  let h = 0;
  for (let i = 0; i < str.length; i++) {
    h = ((h << 5) - h) + str.charCodeAt(i);
    h |= 0;
  }
  return Math.abs(h).toString(36);
}

/** ID текущего пользователя из JWT в localStorage (best-effort). */
function getCurrentUserIdFromToken() {
  try {
    const token = localStorage.getItem('token');
    if (!token) return '';
    const payload = JSON.parse(atob(token.split('.')[1] || ''));
    return String(payload.userId ?? payload.sub ?? payload.id ?? '');
  } catch {
    return '';
  }
}

/**
 * Детерминированный Idempotency-Key.
 * Один и тот же body + URL + userId → один и тот же ключ, поэтому повторный
 * клик «Создать» после оборванной сети не создаёт дубль.
 */
function buildIdempotencyKey(config) {
  const method = (config.method || 'POST').toLowerCase();

  // FormData / Blob / нестандартные тела — не хешируем (стабильно не сериализуются)
  if (typeof FormData !== 'undefined' && config.data instanceof FormData) {
    return `${method}-form-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  }
  if (typeof Blob !== 'undefined' && config.data instanceof Blob) {
    return `${method}-blob-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  }

  const userId = getCurrentUserIdFromToken();
  const url = config.url || '';
  let bodyStr = '';
  try {
    bodyStr = config.data === undefined ? '' : (typeof config.data === 'string' ? config.data : JSON.stringify(config.data));
  } catch {
    bodyStr = '';
  }

  return `${method}-${stableHash(`${userId}:${url}:${bodyStr}`)}`;
}

// ─── Прозрачный авто-ретрай на сетевые ошибки ──────────────────────────────
//
// Если VPN отвалился на пару секунд / соединение дёрнулось — axios получает
// ERR_NETWORK / ECONNABORTED и роняет промис. Без ретрая юзер видит «нет
// связи» и сам жмёт «Создать» ещё раз, плодя дубли. Здесь делаем 3 попытки
// с backoff (1с / 2с / 4с) с тем же Idempotency-Key, прежде чем сдаться.
// Идемпотентность защитит, даже если первая попытка на самом деле дошла.

const MAX_NETWORK_RETRIES = 3;

function isTransientNetworkError(error) {
  if (!error) return false;
  // Пользователь сам отменил — не ретраим
  if (error.name === 'CanceledError' || error.code === 'ERR_CANCELED') return false;
  // Нет ответа от сервера — это сетевая проблема
  if (error.response) {
    // 5xx тоже разово стоит ретрайнуть (сервер мог рестартовать)
    return error.response.status >= 500 && error.response.status < 600;
  }
  if (error.code === 'ERR_NETWORK') return true;
  if (error.code === 'ECONNABORTED') return true; // таймаут
  if (typeof error.message === 'string' && error.message.toLowerCase().includes('network error')) return true;
  return false;
}

function delay(ms) { return new Promise((r) => setTimeout(r, ms)); }

// Обработка ошибок
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (isSessionAuthError(error)) {
      triggerSessionExpired(SESSION_EXPIRED_MESSAGE);
      return Promise.reject(error);
    }

    const config = error.config;
    if (config && !config.skipNetworkRetry && isTransientNetworkError(error)) {
      config.__retryCount = (config.__retryCount || 0) + 1;
      if (config.__retryCount <= MAX_NETWORK_RETRIES) {
        const wait = Math.min(1000 * Math.pow(2, config.__retryCount - 1), 4000);
        console.warn(`🔁 [apiClient] network retry ${config.__retryCount}/${MAX_NETWORK_RETRIES} after ${wait}ms — ${config.method?.toUpperCase()} ${config.url}`);
        await delay(wait);
        return apiClient.request(config);
      }
    }

    return Promise.reject(error);
  }
);

export default apiClient;

// API методы
export const authAPI = {
  login: (username, password, secondFactor = {}) =>
    apiClient.post('/auth/login', { username, password, ...secondFactor }, { timeout: 30000 }),
  verify: () => apiClient.get('/auth/verify', { timeout: 10000, skipNetworkRetry: true }),
  getMe: () => apiClient.get('/auth/me', { timeout: 30000 }),
  getTwoFactorStatus: () => apiClient.get('/auth/2fa/status'),
  initTwoFactorSetup: (currentPassword) => apiClient.post('/auth/2fa/setup/init', { currentPassword }),
  verifyTwoFactorSetup: (otpCode) => apiClient.post('/auth/2fa/setup/verify', { otpCode }),
  disableTwoFactor: (currentPassword, otpCode) => apiClient.post('/auth/2fa/disable', { currentPassword, otpCode }),
  regenerateRecoveryCodes: (currentPassword, otpCode) =>
    apiClient.post('/auth/2fa/recovery-codes/regenerate', { currentPassword, otpCode })
};

export const healthAPI = {
  // Без ретраев: быстрый ping, иначе фоновые таймауты копятся и баннер «залипает»
  check: () => apiClient.get('/health', { timeout: 8000, skipNetworkRetry: true })
};

export const shiftsAPI = {
  getActive: () => apiClient.get('/shifts/active'),
  getHistory: (limit = 50) => apiClient.get(`/shifts/history?limit=${limit}`),
  start: (addressId, selfieFilename = null) =>
    apiClient.post('/shifts/start', { addressId, selfieFilename }),
  end: (shiftId, endSelfieFilename = null) =>
    apiClient.post(`/shifts/end/${shiftId}`, { endSelfieFilename })
};

export const addressesAPI = {
  getAll: () => apiClient.get('/addresses/all'),
  getUserAddresses: () => apiClient.get('/addresses/user'),
  getWithActiveShifts: () => apiClient.get('/addresses/with-active-shifts')
};

export const requestsAPI = {
  getUserRequests: (limit = 50) => apiClient.get(`/requests/user?limit=${limit}`),
  getAllRequests: (limit = 100, status = null, addressIds = null, offset = 0, dateFrom = null, dateTo = null, creatorId = null, feed = false) => {
    let url = `/requests/all?limit=${limit}&offset=${offset}`;
    if (status) url += `&status=${status}`;
    if (addressIds) url += `&addressIds=${addressIds}`;
    if (dateFrom) url += `&dateFrom=${dateFrom}`;
    if (dateTo) url += `&dateTo=${dateTo}`;
    if (creatorId) url += `&creatorId=${creatorId}`;
    if (feed) url += '&feed=true';
    return apiClient.get(url);
  },
  getById: (requestId) => apiClient.get(`/requests/${requestId}`),
  create: (data) => apiClient.post('/requests/create', data),
  delete: (requestId) => apiClient.delete(`/requests/${requestId}`),
  update: (requestId, data) => apiClient.put(`/requests/${requestId}`, typeof data === 'string' ? { description: data } : data),
  updateStatus: (requestId, status, comment) =>
    apiClient.patch(`/requests/${requestId}/status`, { status, comment })
};

export const reportsAPI = {
  getUserReports: (limit = 50) => apiClient.get(`/reports/user?limit=${limit}`),
  getAllReports: (limit = 100, category = null, addressIds = null, offset = 0, dateFrom = null, dateTo = null, creatorId = null, feed = false) => {
    let url = `/reports/all?limit=${limit}&offset=${offset}`;
    if (category) url += `&category=${category}`;
    if (addressIds) url += `&addressIds=${addressIds}`;
    if (dateFrom) url += `&dateFrom=${dateFrom}`;
    if (dateTo) url += `&dateTo=${dateTo}`;
    if (creatorId) url += `&creatorId=${creatorId}`;
    if (feed) url += '&feed=true';
    return apiClient.get(url);
  },
  getById: (reportId) => apiClient.get(`/reports/${reportId}`),
  create: (data) => apiClient.post('/reports/create', data),
  delete: (reportId) => apiClient.delete(`/reports/${reportId}`),
  update: (reportId, data) => apiClient.put(`/reports/${reportId}`, typeof data === 'string' ? { description: data } : data)
};

/** Заголовки против кэша Safari (и др.) на GET ремонтов — дублирует политику сервера для старых прокси. */
const repairsNoCacheConfig = {
  headers: { 'Cache-Control': 'no-cache', Pragma: 'no-cache' }
};

export const repairsAPI = {
  getUserRepairs: (limit = 50) =>
    apiClient.get(`/repairs/user?limit=${limit}`, repairsNoCacheConfig),
  getAllRepairs: (limit = 100, status = null, addressIds = null, offset = 0, dateFrom = null, dateTo = null, creatorId = null, feed = false) => {
    let url = `/repairs/all?limit=${limit}&offset=${offset}`;
    if (status) {
      const statusParam = Array.isArray(status) ? status.filter(Boolean).join(',') : status;
      if (statusParam) url += `&status=${encodeURIComponent(statusParam)}`;
    }
    if (addressIds) url += `&addressIds=${addressIds}`;
    if (dateFrom) url += `&dateFrom=${dateFrom}`;
    if (dateTo) url += `&dateTo=${dateTo}`;
    if (creatorId) url += `&creatorId=${creatorId}`;
    if (feed) url += '&feed=true';
    return apiClient.get(url, repairsNoCacheConfig);
  },
  getById: (repairId) => apiClient.get(`/repairs/${repairId}`, repairsNoCacheConfig),
  create: (data) => apiClient.post('/repairs/create', data),
  delete: (repairId) => apiClient.delete(`/repairs/${repairId}`),
  update: (repairId, data) => apiClient.put(`/repairs/${repairId}`, data),
  updateStatus: (repairId, status, completionPhotos, completionComment) =>
    apiClient.patch(`/repairs/${repairId}/status`, { status, completionPhotos, completionComment }),
  search: (query, addressIds = null) => {
    let url = `/repairs/search/${encodeURIComponent(query)}`;
    if (addressIds) url += `?addressIds=${addressIds}`;
    return apiClient.get(url, repairsNoCacheConfig);
  },
  getOpen: () => apiClient.get('/repairs/status/open', repairsNoCacheConfig),
  getPending: () => apiClient.get('/repairs/status/pending', repairsNoCacheConfig),
  close: (repairId, status, comment, photos) =>
    apiClient.post(`/repairs/${repairId}/close`, { status, comment, photos })
};

export const incidentsAPI = {
  create: (addressId, description, photos) =>
    apiClient.post('/incidents', { addressId, description, photos }),
  // Новый endpoint: отчёт о происшествии с severity и is_no_incident
  report: (addressId, photos, description, severity, isNoIncident) =>
    apiClient.post('/incidents/report', {
      address_id: addressId,
      photos,
      description,
      severity,
      is_no_incident: isNoIncident
    }),
  getAll: (limit = 50, offset = 0, options = {}) => {
    const { mine = false, addressIds = null, dateFrom = null, dateTo = null, creatorId = null, feed = false } = options || {};
    let url = `/incidents?limit=${limit}&offset=${offset}`;
    if (mine) url += '&mine=true';
    if (addressIds) url += `&addressIds=${addressIds}`;
    if (dateFrom) url += `&dateFrom=${dateFrom}`;
    if (dateTo) url += `&dateTo=${dateTo}`;
    if (creatorId) url += `&creatorId=${creatorId}`;
    if (feed) url += '&feed=true';
    return apiClient.get(url);
  },
  getById: (id) => apiClient.get(`/incidents/${id}`),
  update: (id, data) => apiClient.put(`/incidents/${id}`, typeof data === 'string' ? { description: data } : data),
  delete: (id) => apiClient.delete(`/incidents/${id}`)
};

export const garbageAPI = {
  createRequest: (addressId, plannedDate, quantity, photos) =>
    apiClient.post('/garbage/request', { addressId, plannedDate, quantity, photos }),
  createReport: (addressId, description, photos) =>
    apiClient.post('/garbage/report', { addressId, description, photos }),
  createRemark: (addressId, description, photos, wizardIntent = null) =>
    apiClient.post('/garbage/remark', {
      addressId,
      description,
      photos,
      ...(wizardIntent ? { wizardIntent } : {}),
    }),
  updateRequest: (id, data) => apiClient.put(`/garbage/request/${id}`, data),
  updateReport: (id, data) => apiClient.put(`/garbage/report/${id}`, data),
  getRequests: (limit = 50, options = {}, offset = 0) => {
    const { mine = false, addressIds = null, dateFrom = null, dateTo = null, creatorId = null, feed = false } = options || {};
    let url = `/garbage/requests?limit=${limit}&offset=${offset}`;
    if (mine) url += '&mine=true';
    if (addressIds) url += `&addressIds=${addressIds}`;
    if (dateFrom) url += `&dateFrom=${dateFrom}`;
    if (dateTo) url += `&dateTo=${dateTo}`;
    if (creatorId) url += `&creatorId=${creatorId}`;
    if (feed) url += '&feed=true';
    return apiClient.get(url);
  },
  getReports: (limit = 50, options = {}, offset = 0) => {
    const { mine = false, addressIds = null, dateFrom = null, dateTo = null, creatorId = null, feed = false } = options || {};
    let url = `/garbage/reports?limit=${limit}&offset=${offset}`;
    if (mine) url += '&mine=true';
    if (addressIds) url += `&addressIds=${addressIds}`;
    if (dateFrom) url += `&dateFrom=${dateFrom}`;
    if (dateTo) url += `&dateTo=${dateTo}`;
    if (creatorId) url += `&creatorId=${creatorId}`;
    if (feed) url += '&feed=true';
    return apiClient.get(url);
  },
  getRemarks: (limit = 50, options = {}, offset = 0) => {
    const { mine = false, addressIds = null, dateFrom = null, dateTo = null, creatorId = null, feed = false } = options || {};
    let url = `/garbage/remarks?limit=${limit}&offset=${offset}`;
    if (mine) url += '&mine=true';
    if (addressIds) url += `&addressIds=${addressIds}`;
    if (dateFrom) url += `&dateFrom=${dateFrom}`;
    if (dateTo) url += `&dateTo=${dateTo}`;
    if (creatorId) url += `&creatorId=${creatorId}`;
    if (feed) url += '&feed=true';
    return apiClient.get(url);
  },
  updateRemark: (id, data) => apiClient.put(`/garbage/remark/${id}`, data),
  deleteRequest: (id) => apiClient.delete(`/garbage/request/${id}`),
  deleteReport: (id) => apiClient.delete(`/garbage/report/${id}`),
  deleteRemark: (id) => apiClient.delete(`/garbage/remark/${id}`),
};

export const toiletsAPI = {
  createRequest: (addressId, toiletType, plannedDate, volume) =>
    apiClient.post('/toilets/request', { addressId, toiletType, plannedDate, volume }),
  createReport: (addressId, toiletType, description, photos) =>
    apiClient.post('/toilets/report', { addressId, toiletType, description, photos }),
  createRemark: (addressId, description, photos, wizardIntent = null) =>
    apiClient.post('/toilets/remark', {
      addressId,
      description,
      photos,
      ...(wizardIntent ? { wizardIntent } : {}),
    }),
  updateRequest: (id, data) => apiClient.put(`/toilets/request/${id}`, data),
  updateReport: (id, data) => apiClient.put(`/toilets/report/${id}`, data),
  getRequests: (limit = 50, options = {}, offset = 0) => {
    const { mine = false, addressIds = null, dateFrom = null, dateTo = null, creatorId = null, feed = false } = options || {};
    let url = `/toilets/requests?limit=${limit}&offset=${offset}`;
    if (mine) url += '&mine=true';
    if (addressIds) url += `&addressIds=${addressIds}`;
    if (dateFrom) url += `&dateFrom=${dateFrom}`;
    if (dateTo) url += `&dateTo=${dateTo}`;
    if (creatorId) url += `&creatorId=${creatorId}`;
    if (feed) url += '&feed=true';
    return apiClient.get(url);
  },
  getReports: (limit = 50, options = {}, offset = 0) => {
    const { mine = false, addressIds = null, dateFrom = null, dateTo = null, creatorId = null, feed = false } = options || {};
    let url = `/toilets/reports?limit=${limit}&offset=${offset}`;
    if (mine) url += '&mine=true';
    if (addressIds) url += `&addressIds=${addressIds}`;
    if (dateFrom) url += `&dateFrom=${dateFrom}`;
    if (dateTo) url += `&dateTo=${dateTo}`;
    if (creatorId) url += `&creatorId=${creatorId}`;
    if (feed) url += '&feed=true';
    return apiClient.get(url);
  },
  getRemarks: (limit = 50, options = {}, offset = 0) => {
    const { mine = false, addressIds = null, dateFrom = null, dateTo = null, creatorId = null, feed = false } = options || {};
    let url = `/toilets/remarks?limit=${limit}&offset=${offset}`;
    if (mine) url += '&mine=true';
    if (addressIds) url += `&addressIds=${addressIds}`;
    if (dateFrom) url += `&dateFrom=${dateFrom}`;
    if (dateTo) url += `&dateTo=${dateTo}`;
    if (creatorId) url += `&creatorId=${creatorId}`;
    if (feed) url += '&feed=true';
    return apiClient.get(url);
  },
  updateRemark: (id, data) => apiClient.put(`/toilets/remark/${id}`, data),
  deleteRequest: (id) => apiClient.delete(`/toilets/request/${id}`),
  deleteReport: (id) => apiClient.delete(`/toilets/report/${id}`),
  deleteRemark: (id) => apiClient.delete(`/toilets/remark/${id}`),
};

export const participantsAPI = {
  createReport: (addressId, reportType, description, participantInfo, houseAddress, photos) =>
    apiClient.post('/participants/report', { addressId, reportType, description, participantInfo, houseAddress, photos }),
  updateReport: (id, data) => apiClient.put(`/participants/report/${id}`, data),
  getReports: (limit = 50, options = {}, offset = 0) => {
    const { mine = false, addressIds = null, dateFrom = null, dateTo = null, creatorId = null, feed = false } = options || {};
    let url = `/participants/reports?limit=${limit}&offset=${offset}`;
    if (mine) url += '&mine=true';
    if (addressIds) url += `&addressIds=${addressIds}`;
    if (dateFrom) url += `&dateFrom=${dateFrom}`;
    if (dateTo) url += `&dateTo=${dateTo}`;
    if (creatorId) url += `&creatorId=${creatorId}`;
    if (feed) url += '&feed=true';
    return apiClient.get(url);
  },
  getAll: (addressId = null, limit = 100, options = {}) => {
    const { mine = false } = options || {};
    return apiClient.get(`/participants?${addressId ? `addressId=${addressId}&` : ''}limit=${limit}${mine ? '&mine=true' : ''}`);
  },
  getById: (id) => apiClient.get(`/participants/${id}`),
  delete: (id) => apiClient.delete(`/participants/${id}`)
};

export const staffAPI = {
  createReport: (addressId, category, actionType, comment, photos) =>
    apiClient.post('/staff/report', { addressId, category, actionType, comment, photos }),
  updateReport: (id, data) => apiClient.put(`/staff/reports/${id}`, data),
  getReports: (category = null, limit = 50, options = {}, offset = 0) => {
    const { mine = false, addressIds = null, dateFrom = null, dateTo = null, creatorId = null, feed = false } = options || {};
    let url = `/staff/reports?${category ? `category=${category}&` : ''}limit=${limit}&offset=${offset}`;
    if (mine) url += '&mine=true';
    if (addressIds) url += `&addressIds=${addressIds}`;
    if (dateFrom) url += `&dateFrom=${dateFrom}`;
    if (dateTo) url += `&dateTo=${dateTo}`;
    if (creatorId) url += `&creatorId=${creatorId}`;
    if (feed) url += '&feed=true';
    return apiClient.get(url);
  },
  getById: (id) => apiClient.get(`/staff/reports/${id}`),
  deleteReport: (id) => apiClient.delete(`/staff/reports/${id}`)
};

export const snowAPI = {
  createRequest: (addressId, plannedDate, volume, photos) =>
    apiClient.post('/snow/request', { addressId, plannedDate, volume, photos }),
  createReport: (addressId, description, photos) =>
    apiClient.post('/snow/report', { addressId, description, photos }),
  createRemark: (addressId, description, photos) =>
    apiClient.post('/snow/remark', { addressId, description, photos }),
  updateRequest: (id, data) => apiClient.put(`/snow/request/${id}`, data),
  updateReport: (id, data) => apiClient.put(`/snow/report/${id}`, data),
  updateRemark: (id, data) => apiClient.put(`/snow/remark/${id}`, data),
  getRequests: (limit = 50, options = {}, offset = 0) => {
    const { mine = false, addressIds = null, dateFrom = null, dateTo = null, creatorId = null, feed = false } = options || {};
    let url = `/snow/requests?limit=${limit}&offset=${offset}`;
    if (mine) url += '&mine=true';
    if (addressIds) url += `&addressIds=${addressIds}`;
    if (dateFrom) url += `&dateFrom=${dateFrom}`;
    if (dateTo) url += `&dateTo=${dateTo}`;
    if (creatorId) url += `&creatorId=${creatorId}`;
    if (feed) url += '&feed=true';
    return apiClient.get(url);
  },
  getReports: (limit = 50, options = {}, offset = 0) => {
    const { mine = false, addressIds = null, dateFrom = null, dateTo = null, creatorId = null, feed = false } = options || {};
    let url = `/snow/reports?limit=${limit}&offset=${offset}`;
    if (mine) url += '&mine=true';
    if (addressIds) url += `&addressIds=${addressIds}`;
    if (dateFrom) url += `&dateFrom=${dateFrom}`;
    if (dateTo) url += `&dateTo=${dateTo}`;
    if (creatorId) url += `&creatorId=${creatorId}`;
    if (feed) url += '&feed=true';
    return apiClient.get(url);
  },
  getRemarks: (limit = 50, options = {}, offset = 0) => {
    const { mine = false, addressIds = null, dateFrom = null, dateTo = null, creatorId = null, feed = false } = options || {};
    let url = `/snow/remarks?limit=${limit}&offset=${offset}`;
    if (mine) url += '&mine=true';
    if (addressIds) url += `&addressIds=${addressIds}`;
    if (dateFrom) url += `&dateFrom=${dateFrom}`;
    if (dateTo) url += `&dateTo=${dateTo}`;
    if (creatorId) url += `&creatorId=${creatorId}`;
    if (feed) url += '&feed=true';
    return apiClient.get(url);
  },
  deleteRequest: (id) => apiClient.delete(`/snow/request/${id}`),
  deleteReport: (id) => apiClient.delete(`/snow/report/${id}`),
  deleteRemark: (id) => apiClient.delete(`/snow/remark/${id}`),
};

export const deratizationAPI = {
  createReport: (addressId, description, photos) =>
    apiClient.post('/deratization/report', { addressId, description, photos }),
  updateReport: (id, data) => apiClient.put(`/deratization/report/${id}`, data),
  getReports: (limit = 50, options = {}, offset = 0) => {
    const { mine = false, addressIds = null, dateFrom = null, dateTo = null, creatorId = null, feed = false } = options || {};
    let url = `/deratization/reports?limit=${limit}&offset=${offset}`;
    if (mine) url += '&mine=true';
    if (addressIds) url += `&addressIds=${addressIds}`;
    if (dateFrom) url += `&dateFrom=${dateFrom}`;
    if (dateTo) url += `&dateTo=${dateTo}`;
    if (creatorId) url += `&creatorId=${creatorId}`;
    if (feed) url += '&feed=true';
    return apiClient.get(url);
  },
  getRequests: (limit = 50, options = {}, offset = 0) => {
    const { mine = false, addressIds = null, dateFrom = null, dateTo = null, creatorId = null, feed = false } = options || {};
    let url = `/deratization/requests?limit=${limit}&offset=${offset}`;
    if (mine) url += '&mine=true';
    if (addressIds) url += `&addressIds=${addressIds}`;
    if (dateFrom) url += `&dateFrom=${dateFrom}`;
    if (dateTo) url += `&dateTo=${dateTo}`;
    if (creatorId) url += `&creatorId=${creatorId}`;
    if (feed) url += '&feed=true';
    return apiClient.get(url);
  },
  deleteReport: (id) => apiClient.delete(`/deratization/report/${id}`),
  deleteRequest: (id) => apiClient.delete(`/deratization/request/${id}`),
};

export const animationAPI = {
  createReport: (addressId, type, description, photos) =>
    apiClient.post('/animation/report', { addressId, type, description, photos }),
  updateReport: (id, data) => apiClient.put(`/animation/reports/${id}`, data),
  getReports: (type = null, limit = 50, options = {}, offset = 0) => {
    const { mine = false, addressIds = null, dateFrom = null, dateTo = null, creatorId = null, feed = false } = options || {};
    let url = `/animation/reports?limit=${limit}&offset=${offset}`;
    if (type) url += `&type=${type}`;
    if (mine) url += '&mine=true';
    if (addressIds) url += `&addressIds=${addressIds}`;
    if (dateFrom) url += `&dateFrom=${dateFrom}`;
    if (dateTo) url += `&dateTo=${dateTo}`;
    if (creatorId) url += `&creatorId=${creatorId}`;
    if (feed) url += '&feed=true';
    return apiClient.get(url);
  },
  deleteReport: (id) => apiClient.delete(`/animation/reports/${id}`)
};

export const plantsAPI = {
  createRequest: (addressId, description, photos) =>
    apiClient.post('/plants/request', { addressId, description, photos }),
  createApproval: (addressId, description, photos) =>
    apiClient.post('/plants/approval', { addressId, description, photos }),
  updateRequest: (id, data) => apiClient.put(`/plants/request/${id}`, data),
  updateApproval: (id, data) => apiClient.put(`/plants/approval/${id}`, data),
  getRequests: (limit = 50, options = {}, offset = 0) => {
    const { addressIds = null, dateFrom = null, dateTo = null, creatorId = null, feed = false } = options || {};
    let url = `/plants/requests?limit=${limit}&offset=${offset}`;
    if (addressIds) url += `&addressIds=${addressIds}`;
    if (dateFrom) url += `&dateFrom=${dateFrom}`;
    if (dateTo) url += `&dateTo=${dateTo}`;
    if (creatorId) url += `&creatorId=${creatorId}`;
    if (feed) url += '&feed=true';
    return apiClient.get(url);
  },
  getApprovals: (limit = 50, options = {}, offset = 0) => {
    const { dateFrom = null, dateTo = null } = options || {};
    let url = `/plants/approvals?limit=${limit}&offset=${offset}`;
    if (dateFrom) url += `&dateFrom=${dateFrom}`;
    if (dateTo) url += `&dateTo=${dateTo}`;
    return apiClient.get(url);
  },
  deleteRequest: (id) => apiClient.delete(`/plants/request/${id}`),
  deleteApproval: (id) => apiClient.delete(`/plants/approval/${id}`),
};

export const propertyAPI = {
  createReport: (addressId, category, action, comment, photos) =>
    apiClient.post('/property/report', { addressId, category, action, comment, photos }),
  updateReport: (id, data) => apiClient.put(`/property/reports/${id}`, data),
  getReports: (category = null, limit = 50, options = {}, offset = 0) => {
    const { mine = false, addressIds = null, dateFrom = null, dateTo = null, creatorId = null, feed = false } = options || {};
    let url = `/property/reports?${category ? `category=${category}&` : ''}limit=${limit}&offset=${offset}`;
    if (mine) url += '&mine=true';
    if (addressIds) url += `&addressIds=${addressIds}`;
    if (dateFrom) url += `&dateFrom=${dateFrom}`;
    if (dateTo) url += `&dateTo=${dateTo}`;
    if (creatorId) url += `&creatorId=${creatorId}`;
    if (feed) url += '&feed=true';
    return apiClient.get(url);
  },
  deleteReport: (id) => apiClient.delete(`/property/reports/${id}`)
};

export const mountingAPI = {
  createReport: (addressId, category, action, comment, photos) =>
    apiClient.post('/mounting/report', { addressId, category, action, comment, photos }),
  updateReport: (id, data) => apiClient.put(`/mounting/reports/${id}`, data),
  getReports: (category = null, limit = 50, options = {}, offset = 0) => {
    const { mine = false, addressIds = null, dateFrom = null, dateTo = null, creatorId = null, feed = false } = options || {};
    let url = `/mounting/reports?${category ? `category=${category}&` : ''}limit=${limit}&offset=${offset}`;
    if (mine) url += '&mine=true';
    if (addressIds) url += `&addressIds=${addressIds}`;
    if (dateFrom) url += `&dateFrom=${dateFrom}`;
    if (dateTo) url += `&dateTo=${dateTo}`;
    if (creatorId) url += `&creatorId=${creatorId}`;
    if (feed) url += '&feed=true';
    return apiClient.get(url);
  },
  deleteReport: (id) => apiClient.delete(`/mounting/reports/${id}`)
};

export const decorAPI = {
  createReport: (addressId, category, location, action, comment, photos) =>
    apiClient.post('/decor/report', { addressId, category, location, action, comment, photos }),
  updateReport: (id, data) => apiClient.put(`/decor/reports/${id}`, data),
  getReports: (category = null, limit = 50, options = {}, offset = 0) => {
    const { mine = false, addressIds = null, dateFrom = null, dateTo = null, creatorId = null, feed = false } = options || {};
    let url = `/decor/reports?${category ? `category=${category}&` : ''}limit=${limit}&offset=${offset}`;
    if (mine) url += '&mine=true';
    if (addressIds) url += `&addressIds=${addressIds}`;
    if (dateFrom) url += `&dateFrom=${dateFrom}`;
    if (dateTo) url += `&dateTo=${dateTo}`;
    if (creatorId) url += `&creatorId=${creatorId}`;
    if (feed) url += '&feed=true';
    return apiClient.get(url);
  },
  deleteReport: (id) => apiClient.delete(`/decor/reports/${id}`)
};

export const ormmAPI = {
  createReport: (addressId, type, description, photos) =>
    apiClient.post('/ormm/report', { addressId, type, description, photos }),
  updateReport: (id, data) => apiClient.put(`/ormm/reports/${id}`, data),
  getReports: (type = null, limit = 50, options = {}, offset = 0) => {
    const { mine = false, addressIds = null, dateFrom = null, dateTo = null, creatorId = null, feed = false } = options || {};
    let url = `/ormm/reports?${type ? `type=${type}&` : ''}limit=${limit}&offset=${offset}`;
    if (mine) url += '&mine=true';
    if (addressIds) url += `&addressIds=${addressIds}`;
    if (dateFrom) url += `&dateFrom=${dateFrom}`;
    if (dateTo) url += `&dateTo=${dateTo}`;
    if (creatorId) url += `&creatorId=${creatorId}`;
    if (feed) url += '&feed=true';
    return apiClient.get(url);
  },
  deleteReport: (id) => apiClient.delete(`/ormm/reports/${id}`)
};

export const approvalsAPI = {
  getByAddress: (addressId) => apiClient.get(`/approvals?addressId=${addressId}`),
  getAddresses: (approvalId) => apiClient.get(`/approvals/addresses/${approvalId}`),
  search: ({ query, field, dateFrom, dateTo, addressId } = {}) => {
    const params = new URLSearchParams();
    if (query) params.set('q', query);
    if (field) params.set('field', field);
    if (dateFrom) params.set('dateFrom', dateFrom);
    if (dateTo) params.set('dateTo', dateTo);
    if (addressId) params.set('addressId', addressId);
    return apiClient.get(`/approvals/search?${params.toString()}`);
  },
  delete: (approvalId) => apiClient.delete(`/approvals/${approvalId}`),
  create: (data) => apiClient.post('/approvals', data),
  update: (approvalId, data) => apiClient.put(`/approvals/${approvalId}`, data),
};

export const visitorsAPI = {
  createCount: (addressId, period, count) =>
    apiClient.post('/visitors/count', { addressId, period, count }),
  getCounts: (addressId = null, period = null, date = null, limit = 50, options = {}, offset = 0) => {
    const { mine = false, addressIds = null, dateFrom = null, dateTo = null, creatorId = null, feed = false } = options || {};
    let url = `/visitors/counts?${addressId ? `addressId=${addressId}&` : ''}${period ? `period=${period}&` : ''}${date ? `date=${date}&` : ''}limit=${limit}&offset=${offset}`;
    if (mine) url += '&mine=true';
    if (addressIds) url += `&addressIds=${addressIds}`;
    if (dateFrom) url += `&dateFrom=${dateFrom}`;
    if (dateTo) url += `&dateTo=${dateTo}`;
    if (creatorId) url += `&creatorId=${creatorId}`;
    if (feed) url += '&feed=true';
    return apiClient.get(url);
  },
  updateCount: (id, count) =>
    apiClient.patch(`/visitors/counts/${id}`, { count }),
  deleteCount: (id) => apiClient.delete(`/visitors/counts/${id}`)
};

export const trashAPI = {
  getAll: (entityType = null) =>
    apiClient.get(`/trash${entityType ? `?entity_type=${entityType}` : ''}`),
  restore: (trashId) => apiClient.post(`/trash/${trashId}/restore`),
  permanentDelete: (trashId) => apiClient.delete(`/trash/${trashId}`)
};

export const historyAPI = {
  getHistory: (entityType, entityId) =>
    apiClient.get(`/history?entityType=${entityType}&entityId=${entityId}`)
};

export const commentsAPI = {
  getComments: (entityType, entityId) =>
    apiClient.get(`/comments?entityType=${entityType}&entityId=${entityId}`),
  postComment: (entityType, entityId, text) =>
    apiClient.post('/comments', { entityType, entityId, text }),
  deleteComment: (commentId) => apiClient.delete(`/comments/${commentId}`),
};

export const uploadsAPI = {
  uploadSingle: (file) => {
    const formData = new FormData();
    formData.append('photo', file);
    return apiClient.post('/uploads/single', formData, {
      timeout: 120_000,
    });
  },
  uploadMultiple: (files) => {
    const formData = new FormData();
    files.forEach(file => formData.append('photos', file));
    return apiClient.post('/uploads/multiple', formData, {
      timeout: 120_000,
    });
  },
  uploadDocument: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return apiClient.post('/uploads/document', formData, {
      timeout: 120_000, // 2 минуты — документы могут быть большими
    });
  },
  getPhoto: (filename) => {
    if (!filename) return null;
    const clean = filename
      .replace(/^\/api\/uploads\//, '')
      .replace(/^\/app\/photos\//, '')
      .replace(/^\/uploads\//, '')
      .replace(/^uploads\//, '');
    // Telegram file_id не имеют расширения — их нельзя отдать через /api/uploads/
    if (!/\.[a-zA-Z0-9]{2,5}$/.test(clean)) return null;
    return `${API_BASE_URL}/uploads/${clean}`;
  },
  deletePhoto: (filename) => apiClient.delete(`/uploads/${filename}`)
};

export const registeredParticipantsAPI = {
  getAll: (addressId = null, limit = 100, options = {}, offset = 0) => {
    const { mine = false, dateFrom = null, dateTo = null, creatorId = null, feed = false } = options || {};
    let url = `/registered-participants?${addressId ? `addressId=${addressId}&` : ''}limit=${limit}&offset=${offset}${mine ? '&mine=true' : ''}`;
    if (dateFrom) url += `&dateFrom=${dateFrom}`;
    if (dateTo) url += `&dateTo=${dateTo}`;
    if (creatorId) url += `&creatorId=${creatorId}`;
    if (feed) url += '&feed=true';
    return apiClient.get(url);
  },
  getById: (id) => apiClient.get(`/registered-participants/${id}`),
  create: (data) => apiClient.post('/registered-participants', data),
  update: (id, data) => apiClient.put(`/registered-participants/${id}`, data),
  delete: (id) => apiClient.delete(`/registered-participants/${id}`)
};

export const bugReportsAPI = {
  create: (title, description, screenshot, device, os, browser, browserVersion) =>
    apiClient.post('/bug-reports', {
      title, description, screenshot,
      device, os, browser, browser_version: browserVersion,
    }),
  getAll: (filters = {}) => {
    const params = new URLSearchParams();
    if (filters.status)      params.append('status', filters.status);
    if (filters.assignee_id) params.append('assignee_id', filters.assignee_id);
    if (filters.search)      params.append('search', filters.search);
    const qs = params.toString();
    return apiClient.get(`/bug-reports${qs ? `?${qs}` : ''}`);
  },
  getMy: () => apiClient.get('/bug-reports/my'),
  getById: (id) => apiClient.get(`/bug-reports/${id}`),
  updateStatus: (id, status, resolution_comment) =>
    apiClient.patch(`/bug-reports/${id}/status`, { status, resolution_comment }),
  assign: (id, assignee_id) =>
    apiClient.patch(`/bug-reports/${id}/assign`, { assignee_id }),
  remove: (id) => apiClient.delete(`/bug-reports/${id}`),
};

export const settingsAPI = {
  getMaxSettings: () => apiClient.get('/settings/max'),
  updateMaxSettings: (settings) => apiClient.put('/settings/max', { settings }),
  getMaxCustomChats: () => apiClient.get('/settings/max/custom-chats'),
  createMaxCustomChat: (data) => apiClient.post('/settings/max/custom-chats', data),
  testMaxChat: (chatKey) => apiClient.post('/settings/max/test', { chatKey }),
};

export const sectionHelpAPI = {
  getAll: () => apiClient.get('/section-help'),
  adminList: () => apiClient.get('/section-help/admin/list'),
  upload: (sectionKey, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return apiClient.put(`/section-help/admin/${sectionKey}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 120_000,
    });
  },
  remove: (sectionKey) => apiClient.delete(`/section-help/admin/${sectionKey}`),
};

export const docsAPI = {
  getAll: (search) => {
    const qs = search?.trim() ? `?search=${encodeURIComponent(search.trim())}` : '';
    return apiClient.get(`/docs${qs}`);
  },
  getById: (id) => apiClient.get(`/docs/${id}`),
  create: (data) => apiClient.post('/docs', data),
  update: (id, data) => apiClient.put(`/docs/${id}`, data),
  remove: (id) => apiClient.delete(`/docs/${id}`),
};

export const feedReadAPI = {
  getState: () => apiClient.get('/feed-read/state'),
  markAll: () => apiClient.post('/feed-read/mark-all'),
  markSection: (sectionKey) => apiClient.post('/feed-read/mark-section', { sectionKey }),
  markItem: (itemId) => apiClient.post('/feed-read/mark-item', { itemId }),
};

export const usersAPI = {
  getAll: () => apiClient.get('/users'),
  getAllWithAddressesAndShifts: () => apiClient.get('/users/with-addresses-and-shifts'),
  getById: (userId) => apiClient.get(`/users/${userId}`),
  create: (username, password, fullName, role, extra = {}) =>
    apiClient.post('/users', { username, password, fullName, role, ...extra }),
  update: (userId, data) => apiClient.put(`/users/${userId}`, data),
  delete: (userId) => apiClient.delete(`/users/${userId}`),
  block: (userId, isAuthorized) =>
    apiClient.patch(`/users/${userId}/block`, { isAuthorized }),
  getAddresses: (userId) => apiClient.get(`/users/${userId}/addresses`),
  addAddress: (userId, addressId) =>
    apiClient.post(`/users/${userId}/addresses`, { addressId }),
  removeAddress: (userId, addressId) =>
    apiClient.delete(`/users/${userId}/addresses/${addressId}`)
};

export const permissionsAPI = {
  getMine: () => apiClient.get('/permissions/me'),
  getSections: () => apiClient.get('/permissions/sections')
};

export const permissionTemplatesAPI = {
  getAll: () => apiClient.get('/admin/permission-templates'),
  getById: (id) => apiClient.get(`/admin/permission-templates/${id}`),
  create: (data) => apiClient.post('/admin/permission-templates', data),
  update: (id, data) => apiClient.put(`/admin/permission-templates/${id}`, data),
  delete: (id) => apiClient.delete(`/admin/permission-templates/${id}`)
};

export const guestResourcesAPI = {
  get: (userId, type = 'address') =>
    apiClient.get(`/admin/guest-resources/${userId}?type=${type}`),
  set: (userId, type, ids) =>
    apiClient.put(`/admin/guest-resources/${userId}`, { type, ids })
};

export const feedItemAPI = {
  getById: (entityType, entityId) => apiClient.get(`/feed/item/${entityType}/${entityId}`),
};

export const guestAuditAPI = {
  getEntries: (filters = {}) => {
    const params = new URLSearchParams();
    if (filters.userId)  params.append('userId',  filters.userId);
    if (filters.section) params.append('section', filters.section);
    if (filters.result)  params.append('result',  filters.result);
    if (filters.limit)   params.append('limit',   filters.limit);
    if (filters.offset)  params.append('offset',  filters.offset);
    const qs = params.toString();
    return apiClient.get(`/admin/guest-audit${qs ? `?${qs}` : ''}`);
  }
};

export const customSectionsAPI = {
  getRegistry: () => apiClient.get('/custom-sections/registry'),
  getAll: () => apiClient.get('/custom-sections/all'),
  getByKey: (sectionKey) => apiClient.get(`/custom-sections/${sectionKey}`),
  create: (data) => apiClient.post('/custom-sections', data),
  update: (sectionKey, data) => apiClient.put(`/custom-sections/${sectionKey}`, data),
  remove: (sectionKey) => apiClient.delete(`/custom-sections/${sectionKey}`),
  getCards: (sectionKey, options = {}) => {
    const params = new URLSearchParams();
    const {
      limit = 50, offset = 0, mine = false, feed = false,
      addressIds, creatorId, dateFrom, dateTo, cardId, extraFilters = {},
    } = options;
    params.set('limit', limit);
    params.set('offset', offset);
    if (mine) params.set('mine', 'true');
    if (feed) params.set('feed', 'true');
    if (addressIds?.length) params.set('addressIds', addressIds.join(','));
    if (creatorId) params.set('creatorId', creatorId);
    if (dateFrom) params.set('dateFrom', dateFrom);
    if (dateTo) params.set('dateTo', dateTo);
    if (cardId) params.set('cardId', cardId);
    for (const [k, v] of Object.entries(extraFilters)) {
      if (v?.length) params.set(`f_${k}`, Array.isArray(v) ? v.join(',') : v);
    }
    return apiClient.get(`/custom-sections/${sectionKey}/cards?${params.toString()}`);
  },
  getCard: (sectionKey, cardId) => apiClient.get(`/custom-sections/${sectionKey}/cards/${cardId}`),
  createCard: (sectionKey, data) => apiClient.post(`/custom-sections/${sectionKey}/cards`, data),
  updateCard: (sectionKey, cardId, data) => apiClient.put(`/custom-sections/${sectionKey}/cards/${cardId}`, data),
  deleteCard: (sectionKey, cardId) => apiClient.delete(`/custom-sections/${sectionKey}/cards/${cardId}`),
};
